/** Lightweight VEVENT / METHOD helpers for mail calendar invites (Tip 213). */

export type CalendarInvite = {
  uid: string;
  summary?: string;
  description?: string;
  location?: string;
  start?: string;
  end?: string;
  organizerEmail?: string;
  organizerName?: string;
  method?: string;
  sequence?: number;
  raw?: string;
};

export type RsvpPartStat = "ACCEPTED" | "DECLINED" | "TENTATIVE";

export type RsvpStatus = "accepted" | "declined" | "tentative";

const FOLD = /\r?\n[ \t]/g;

function unfoldIcs(text: string): string {
  return String(text || "").replace(/\r\n/g, "\n").replace(FOLD, "");
}

function stripValue(raw: string): string {
  let value = raw.trim();
  if (value.startsWith('"') && value.endsWith('"')) value = value.slice(1, -1);
  return value.replace(/\\n/gi, "\n").replace(/\\,/g, ",").replace(/\\;/g, ";").replace(/\\\\/g, "\\");
}

function propValue(line: string): { name: string; params: string; value: string } | null {
  const colon = line.indexOf(":");
  if (colon < 0) return null;
  const left = line.slice(0, colon);
  const value = line.slice(colon + 1);
  const semi = left.indexOf(";");
  const name = (semi >= 0 ? left.slice(0, semi) : left).toUpperCase();
  const params = semi >= 0 ? left.slice(semi + 1) : "";
  return { name, params, value };
}

function mailtoEmail(raw: string): string {
  const text = stripValue(raw).trim();
  const match = /mailto:([^\s>;]+)/i.exec(text);
  if (match) return match[1].trim().toLowerCase();
  if (text.includes("@")) return text.replace(/^<|>$/g, "").trim().toLowerCase();
  return "";
}

/** Convert ICS date (UTC or floating / TZID) to ISO-ish string for display. */
function icsDateToIso(value: string, params: string): string | undefined {
  const raw = stripValue(value).trim();
  if (!raw) return undefined;
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/.exec(raw);
  if (!m) return raw;
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}${m[7] || ""}`;
  if (m[7]) return new Date(iso).toISOString();
  const tzid = /TZID=([^;:]+)/i.exec(params)?.[1];
  if (tzid) return `${iso} (${tzid})`;
  return iso;
}

export function parseIcs(text: string): CalendarInvite | null {
  const unfolded = unfoldIcs(text);
  if (!/BEGIN:VEVENT/i.test(unfolded)) return null;

  let method = "";
  let uid = "";
  let summary = "";
  let description = "";
  let location = "";
  let start = "";
  let end = "";
  let organizerEmail = "";
  let organizerName = "";
  let sequence = 0;
  let inEvent = false;

  for (const line of unfolded.split("\n")) {
    const prop = propValue(line.trim());
    if (!prop) continue;
    if (prop.name === "BEGIN" && prop.value.toUpperCase() === "VEVENT") {
      inEvent = true;
      continue;
    }
    if (prop.name === "END" && prop.value.toUpperCase() === "VEVENT") {
      inEvent = false;
      continue;
    }
    if (prop.name === "METHOD") {
      method = stripValue(prop.value).toUpperCase();
      continue;
    }
    if (!inEvent) continue;
    if (prop.name === "UID") uid = stripValue(prop.value);
    else if (prop.name === "SUMMARY") summary = stripValue(prop.value);
    else if (prop.name === "DESCRIPTION") description = stripValue(prop.value);
    else if (prop.name === "LOCATION") location = stripValue(prop.value);
    else if (prop.name === "DTSTART") start = icsDateToIso(prop.value, prop.params) || "";
    else if (prop.name === "DTEND") end = icsDateToIso(prop.value, prop.params) || "";
    else if (prop.name === "SEQUENCE") sequence = Number(stripValue(prop.value)) || 0;
    else if (prop.name === "ORGANIZER") {
      organizerEmail = mailtoEmail(prop.value);
      const cn = /CN=([^;:]+)/i.exec(prop.params)?.[1];
      if (cn) organizerName = stripValue(cn.replace(/^"|"$/g, ""));
    }
  }

  if (!uid) return null;
  return {
    uid,
    summary: summary || undefined,
    description: description || undefined,
    location: location || undefined,
    start: start || undefined,
    end: end || undefined,
    organizerEmail: organizerEmail || undefined,
    organizerName: organizerName || undefined,
    method: method || undefined,
    sequence,
    raw: text,
  };
}

export function rsvpStatusToPartStat(status: RsvpStatus): RsvpPartStat {
  if (status === "accepted") return "ACCEPTED";
  if (status === "tentative") return "TENTATIVE";
  return "DECLINED";
}

export function partStatToRsvpStatus(partStat: string): RsvpStatus | "" {
  const value = partStat.trim().toUpperCase();
  if (value === "ACCEPTED") return "accepted";
  if (value === "TENTATIVE") return "tentative";
  if (value === "DECLINED") return "declined";
  return "";
}

function escapeIcsText(value: string): string {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function foldIcsLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  let rest = line;
  chunks.push(rest.slice(0, 75));
  rest = rest.slice(75);
  while (rest.length) {
    chunks.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  return chunks.join("\r\n");
}

function toIcsUtcStamp(value?: string): string {
  if (!value) {
    return new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  }
  const digits = value.replace(/\D/g, "");
  if (digits.length >= 15) return `${digits.slice(0, 15)}Z`;
  if (digits.length === 8) return `${digits}T000000Z`;
  return new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

/** Build a METHOD:REPLY calendar part (Outlook-style optional comment in DESCRIPTION). */
export function buildIcsReply(input: {
  invite: CalendarInvite;
  attendeeEmail: string;
  attendeeName?: string;
  status: RsvpStatus;
  comment?: string;
}): string {
  const partStat = rsvpStatusToPartStat(input.status);
  const stamp = toIcsUtcStamp();
  const dtStart = toIcsUtcStamp(input.invite.start);
  const dtEnd = toIcsUtcStamp(input.invite.end || input.invite.start);
  const uid = input.invite.uid;
  const sequence = Math.max(0, Number(input.invite.sequence) || 0);
  const summary = input.invite.summary || "Meeting";
  const organizer = input.invite.organizerEmail
    ? `ORGANIZER:mailto:${input.invite.organizerEmail}`
    : "";
  const cn = input.attendeeName ? `;CN="${escapeIcsText(input.attendeeName)}"` : "";
  const description = input.comment?.trim()
    ? escapeIcsText(input.comment.trim())
    : escapeIcsText(`${partStat}: ${summary}`);

  const lines = [
    "BEGIN:VCALENDAR",
    "PRODID:-//ATW Hub//Mail RSVP//EN",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:REPLY",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${description}`,
    `SEQUENCE:${sequence}`,
    `STATUS:CONFIRMED`,
    organizer,
    `ATTENDEE${cn};PARTSTAT=${partStat};RSVP=TRUE:mailto:${input.attendeeEmail}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  return lines.map(foldIcsLine).join("\r\n") + "\r\n";
}

export function isCalendarAttachment(file: {
  filename?: string;
  mimeType?: string;
}): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  const name = (file.filename || "").toLowerCase();
  if (mime.includes("text/calendar") || mime.includes("application/ics")) return true;
  if (name.endsWith(".ics") || name.endsWith(".ical") || name.endsWith(".ifb")) return true;
  return false;
}

export function rsvpLabel(status: RsvpStatus | "" | undefined): string {
  if (status === "accepted") return "Accepted";
  if (status === "tentative") return "Tentative";
  if (status === "declined") return "Rejected";
  return "";
}
